import React, { useState } from 'react';
import { 
  Card, 
  List, 
  Typography, 
  Tag, 
  Descriptions, 
  Empty, 
  Button, 
  Modal, 
  Row, 
  Col,
  Space,
  Divider,
  Alert,
  Progress
} from 'antd';
import {
  HeartOutlined,
  EyeOutlined,
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  InfoCircleOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

const PatientPortalDiagnosticsProcedures = ({ data, loading }) => {
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  // Helper function to get procedure type icon and color
  const getProcedureTypeInfo = (procedureType) => {
    const types = {
      'EKG': { icon: <HeartOutlined />, color: '#fa541c' },
      'Echocardiogram': { icon: <HeartOutlined />, color: '#13c2c2' },
      'Stress Test': { icon: <HeartOutlined />, color: '#eb2f96' },
      'Spirometry': { icon: <ExperimentOutlined />, color: '#52c41a' },
      'Colonoscopy': { icon: <MedicineBoxOutlined />, color: '#722ed1' },
      'Endoscopy': { icon: <MedicineBoxOutlined />, color: '#1890ff' },
      'Biopsy': { icon: <ExperimentOutlined />, color: '#fa8c16' },
      'Pathology': { icon: <FileTextOutlined />, color: '#8c8c8c' }
    };
    return types[procedureType] || { icon: <MedicineBoxOutlined />, color: '#8c8c8c' };
  };

  // Helper function to get patient-friendly procedure explanations
  const getProcedureExplanation = (procedureType) => {
    const explanations = {
      'EKG': 'Records the electrical activity of your heart to check for irregular heartbeats and other heart problems.',
      'Echocardiogram': 'Uses sound waves to create moving pictures of your heart to check how well it pumps blood.',
      'Stress Test': 'Monitors your heart while you exercise to see how well it works under physical stress.',
      'Spirometry': 'Measures how much air you can breathe in and out to check your lung function.',
      'Colonoscopy': 'Uses a flexible tube with a camera to examine the inside of your large intestine.',
      'Endoscopy': 'Uses a flexible tube with a camera to examine the inside of your digestive tract.',
      'Biopsy': 'Removes a small sample of tissue for examination under a microscope.',
      'Pathology': 'Laboratory examination of tissue samples to diagnose diseases.'
    };
    return explanations[procedureType] || 'Medical procedure to diagnose or monitor health conditions.';
  };

  // Helper function to determine result significance
  const getResultSignificance = (results) => {
    if (!results) return { level: 'pending', color: 'default', text: 'Pending' };
    
    const normalKeywords = ['normal', 'negative', 'within normal limits', 'unremarkable'];
    const abnormalKeywords = ['abnormal', 'positive', 'elevated', 'decreased', 'irregular'];
    const urgentKeywords = ['urgent', 'immediate', 'critical', 'severe', 'malignant'];
    
    const text = results.toLowerCase();
    
    if (urgentKeywords.some(keyword => text.includes(keyword))) {
      return { level: 'urgent', color: 'red', text: 'Requires Immediate Attention' };
    } else if (abnormalKeywords.some(keyword => text.includes(keyword))) {
      return { level: 'abnormal', color: 'orange', text: 'Abnormal - Follow-up Needed' };
    } else if (normalKeywords.some(keyword => text.includes(keyword))) {
      return { level: 'normal', color: 'green', text: 'Normal Results' };
    } else {
      return { level: 'review', color: 'blue', text: 'Under Review' };
    }
  };

  // Show detailed view of a procedure
  const showDetailModal = (procedure) => {
    setSelectedProcedure(procedure);
    setDetailModalVisible(true);
  };

  if (data.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            No diagnostic procedures found
            <br />
            <Text type="secondary">Your diagnostic procedures will appear here once available</Text>
          </span>
        }
      />
    );
  }

  return (
    <div>
      <List
        dataSource={data}
        renderItem={(procedure) => {
          const procedureInfo = getProcedureTypeInfo(procedure.procedureType);
          const significance = getResultSignificance(procedure.results?.summary);
          
          return (
            <List.Item style={{ padding: 0, marginBottom: 16 }}>
              <Card className="procedure-card" style={{ width: '100%' }}>
                <div className="procedure-header">
                  <div>
                    <Title level={5} className="procedure-title">
                      {React.cloneElement(procedureInfo.icon, { 
                        style: { marginRight: 8, color: procedureInfo.color } 
                      })}
                      {procedure.procedureName || procedure.orderDetails?.procedureName || 'Diagnostic Procedure'}
                    </Title>
                    <Text type="secondary" className="procedure-date">
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      {moment(procedure.procedureDate || procedure.results?.resultDate).format('MMMM DD, YYYY')}
                    </Text>
                    <Tag color={significance.color} style={{ marginLeft: 8 }}>
                      {significance.text}
                    </Tag>
                  </div>
                  <Space>
                    <Button 
                      icon={<EyeOutlined />} 
                      onClick={() => showDetailModal(procedure)}
                      size="small"
                    >
                      View Details
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      size="small"
                      type="text"
                    >
                      Export
                    </Button>
                  </Space>
                </div>

                {/* Procedure explanation */}
                <div style={{ marginBottom: 16, padding: 12, background: '#f6f8fa', borderRadius: 6 }}>
                  <Text style={{ fontSize: 13, color: '#666' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {getProcedureExplanation(procedure.procedureType)}
                  </Text>
                </div>

                {/* Procedure details */}
                <div className="procedure-details">
                  <div className="procedure-detail-item">
                    <div className="procedure-detail-label">Procedure Type</div>
                    <div className="procedure-detail-value">{procedure.procedureType || 'N/A'}</div>
                  </div>
                  <div className="procedure-detail-item">
                    <div className="procedure-detail-label">Performing Provider</div>
                    <div className="procedure-detail-value">{procedure.performingProvider?.name || 'N/A'}</div>
                  </div>
                  <div className="procedure-detail-item">
                    <div className="procedure-detail-label">Location</div>
                    <div className="procedure-detail-value">{procedure.location || 'N/A'}</div>
                  </div>
                  <div className="procedure-detail-item">
                    <div className="procedure-detail-label">Duration</div>
                    <div className="procedure-detail-value">{procedure.duration || 'N/A'}</div>
                  </div>
                </div>

                {/* Results summary */}
                {procedure.results?.summary && (
                  <div className="procedure-results">
                    <div className="procedure-results-title">
                      <Text strong>Results Summary:</Text>
                    </div>
                    <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                      {procedure.results.summary}
                    </Paragraph>
                  </div>
                )}

                {/* Key measurements or findings */}
                {procedure.results?.keyFindings && (
                  <div style={{ marginTop: 16 }}>
                    <Title level={5} style={{ marginBottom: 12 }}>Key Findings:</Title>
                    <Row gutter={[16, 8]}>
                      {procedure.results.keyFindings.map((finding, index) => (
                        <Col key={index} xs={24} sm={12} md={8}>
                          <Card size="small" style={{ background: '#fafafa' }}>
                            <div style={{ textAlign: 'center' }}>
                              <Text strong style={{ fontSize: 16 }}>{finding.value}</Text>
                              <div style={{ fontSize: 12, color: '#8c8c8c' }}>{finding.parameter}</div>
                              {finding.normal !== undefined && (
                                <div style={{ marginTop: 4 }}>
                                  {finding.normal ? (
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                  ) : (
                                    <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />
                                  )}
                                </div>
                              )}
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Provider and order info */}
                <Divider style={{ margin: '16px 0' }} />
                <Row gutter={16}>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Ordered by: {procedure.orderingProvider?.name || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Order Date: {moment(procedure.orderDate).format('MM/DD/YYYY')}
                    </Text>
                  </Col>
                  <Col span={8} style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Status: {procedure.status || 'Completed'}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </List.Item>
          );
        }}
      />

      {/* Detail Modal */}
      <Modal
        title={
          <span>
            <MedicineBoxOutlined style={{ marginRight: 8 }} />
            {selectedProcedure?.procedureName} - Detailed Report
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          <Button key="export" icon={<DownloadOutlined />}>
            Export Report
          </Button>
        ]}
        width={800}
      >
        {selectedProcedure && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Procedure Date">
                {moment(selectedProcedure.procedureDate || selectedProcedure.results?.resultDate).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Performing Provider">
                {selectedProcedure.performingProvider?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Procedure Type">
                {selectedProcedure.procedureType || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                {selectedProcedure.location || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {selectedProcedure.duration || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="green">{selectedProcedure.status || 'Completed'}</Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Indication */}
            {selectedProcedure.indication && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Indication</Title>
                <Paragraph>{selectedProcedure.indication}</Paragraph>
              </div>
            )}

            {/* Procedure Details */}
            {selectedProcedure.procedureDetails && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Procedure Details</Title>
                <Paragraph>{selectedProcedure.procedureDetails}</Paragraph>
              </div>
            )}

            {/* Results */}
            {selectedProcedure.results?.summary && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Results</Title>
                <Paragraph>{selectedProcedure.results.summary}</Paragraph>
              </div>
            )}

            {/* Detailed Findings */}
            {selectedProcedure.results?.detailedFindings && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Detailed Findings</Title>
                <Paragraph>{selectedProcedure.results.detailedFindings}</Paragraph>
              </div>
            )}

            {/* Recommendations */}
            {selectedProcedure.recommendations && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Recommendations</Title>
                <Alert
                  message="Follow-up Instructions"
                  description={selectedProcedure.recommendations}
                  type="info"
                  showIcon
                />
              </div>
            )}

            {/* Next Steps */}
            {selectedProcedure.nextSteps && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Next Steps</Title>
                <Alert
                  message="Action Required"
                  description={selectedProcedure.nextSteps}
                  type="warning"
                  showIcon
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientPortalDiagnosticsProcedures;
